<script>
    import dressing from "@intellectability/platform-common";
    import {ACTIONS, SCOPES} from "@intellectability/platform-common/constants";
    import {actionCheck} from "@intellectability/platform-common/utils";
    import {
        Button,
        FlexList,
        Icon
    } from "@intellectability/platform-common/sv";
    import {userSecurity} from "@intellectability/platform-common/sv/state";
    import {Col, Row} from "@sveltestrap/sveltestrap";
    import {onMount} from "svelte";
    import AssignCourseModal from "./AssignCourseModal.svelte";

    let {id: userID, profile, isActiveProfile} = $props();

    const hasAccess = actionCheck(userSecurity.userActions);
    const userScope = $derived(parseInt(userSecurity.loggedInUser.scope, 10));

    const {USER_TRAINING_NOTES_DELETE, USER_TRAINING_NOTES_EDIT, USER_DETAILS_REQUEST} = ACTIONS;

    let profileIsWithinScope = $derived.by(function () {

        // check state
        if (userScope === SCOPES.STATE) {
            return true;
        }

        const {loggedInUser} = userSecurity;

        // check region
        const isRegionScope = userScope === SCOPES.REGION;
        const profileIsInRegion = profile.region === loggedInUser.region;

        if (isRegionScope) {
            return profileIsInRegion;
        }

        // check provider
        const isProviderScope = userScope === SCOPES.PROVIDER;
        const profileHasSameProvider = profile.employer_id === loggedInUser.employer_id;

        if (isProviderScope) {
            return profileHasSameProvider;
        }

        return false;
    });

    let canDelete = $derived.by(function () {
        // deletion is allowed when
        // 1. The profile is active
        // 2. User has delete permissions
        // 3. The profile is within own scope
        return isActiveProfile && hasAccess(USER_TRAINING_NOTES_DELETE) && profileIsWithinScope;
    });

    // editing is limited it seems to creating a training note
    // after creation
    let canAssignCourses = $derived.by(function () {
        // editing is allowed when
        // 1. The profile is active
        // 2a. User has edit permissions
        // 2b. OR User is a gatekeeper
        // 3. The profile is within own scope
        return isActiveProfile
               && (hasAccess(USER_TRAINING_NOTES_EDIT) || hasAccess(USER_DETAILS_REQUEST))
               && profileIsWithinScope;
    });

    let loading = $state(true);

    let trainingNotes = $state.raw([]);

    // pagination
    let pagination = $state({
        show: true,
        page: 1,
        limit: 10,
        pageLengthMenu: [10, 25, 50, 100, 500]
    });

    let sort = $state({
        field: "training_date",
        order: "asc"
    });

    let columns = $derived([
        {
            collapse: true,
            name: "Training Title",
            field: "training_date",
            sortedBy: sort.field === "training_date" ? sort.order : "",
            sortable: true
        },
        {
            collapse: false,
            name: "Length",
            field: "start_time",
            sortedBy: sort.field === "start_time" ? sort.order : "",
            sortable: true
        },
        {
            collapse: true,
            name: "Series",
            field: "end_time",
            sortedBy: sort.field === "end_time" ? sort.order : "",
            sortable: true
        },
        {
            collapse: false,
            name: "Materials",
            field: "topic",
            sortedBy: sort.field === "topic" ? sort.order : "",
            sortable: true
        },
        {
            collapse: true,
            name: "Optionality",
            field: "trainer",
            sortedBy: sort.field === "trainer" ? sort.order : "",
            sortable: true
        },
        {
            collapse: true,
            name: "Start Date",
            field: "person",
            sortedBy: sort.field === "person" ? sort.order : "",
            sortable: true
        },
        {
            collapse: true,
            name: "End Date",
            field: "person",
            sortedBy: sort.field === "person" ? sort.order : "",
            sortable: true
        },
        {
            collapse: true,
            name: "Status",
            field: "person",
            sortedBy: sort.field === "person" ? sort.order : "",
            sortable: true
        },
        {
            name: "Actions",
            width: "110px",
            buttons: [
                {
                    icon: "circle-play",
                    tooltip: "Launch Course",
                    variant: "success",
                    appearance: "filled",
                    text: "Launch",
                    action(_, row) {

                    },
                    disabled() {
                        return canDelete === false;
                    }
                }
            ]
        }
    ]);

    onMount(function onTrainingNotesMount() {
        dressing.get("/users/training_note_topics")
        .then(({data: res}) => {

            const {success, data} = res;

            if (success === true) {
                // since I'm getting the whole table, I need to break it down into something useful
                const the_whole_table = data.topics;

                /** @type {object} */
                const topicData = the_whole_table.reduce((acc, row) => {
                    const {topic, subtopic, sort_order} = row;

                    if (!acc[topic]) {
                        acc[topic] = {
                            subtopics: [],
                            needsSorting: false
                        };
                    }

                    // Check if this topic needs sorting
                    if (sort_order) {
                        acc[topic].needsSorting = true;
                    }

                    // Add subtopic if it exists
                    if (subtopic) {
                        acc[topic].subtopics.push(row);
                    }

                    return acc;
                }, {});

                // Now build the final structure efficiently

                /**
                 * @type {Topic[]}
                 */
                let training_topics = Object.keys(topicData).map(topic => {
                    let filtered = topicData[topic].subtopics;

                    // Sort if needed
                    if (topicData[topic].needsSorting) {
                        filtered = filtered.sort((a, b) => a.sort_order - b.sort_order);
                    }

                    return {
                        topic,
                        subtopics: filtered.map(e => e.subtopic)
                    };
                });

            }
        });
    });

    $effect(filter_list);

    function filter_list() {

        if (profileIsWithinScope) {
            const sort_query = {
                sort: sort.field || null,
                order: sort.order || null
            };

            const page_query = {
                page: pagination.page || null,
                limit: pagination.limit || null
            };

            dressing.get(`/users/training_notes/${userID}/`, {
                params: Object.assign({...sort_query, ...page_query})
            }).then(({data: res}) => {
                const {success, data} = res;
                if (success === true) {
                    const {notes: t} = data;

                    if (Array.isArray(t)) {
                        trainingNotes = t;
                        pagination.page = data.page || 1;
                        pagination.limit = data.limit || 10;
                        pagination.recordsTotal = data.recordsTotal || 0;
                    }
                }
            }).finally(() => loading = false);
        }
    }

</script>

<wa-details class="overflow-hidden mb-3" open>
    {@render detailsSummary("My Available Courses", "regular", 13)}
    <FlexList
            loading={loading}
            pagerConfig={pagination}
            onpagechange={(page) => pagination.page = page}
            onsort={(field, order) => {sort = {field, order};}}
            onpagelengthchange={(newLimit) => pagination.limit = newLimit}
            {columns}
            data={trainingNotes}
            expandableRows
            expandableRowsComponent={expansionSnippet}
    >
    </FlexList>
</wa-details>

<AssignCourseModal/>

<wa-divider class="my-5"></wa-divider>

<wa-details class="overflow-hidden mb-4">
    {@render detailsSummary("My Completed Courses", "classic")}
    <FlexList
            loading={loading}
            pagerConfig={pagination}
            onpagechange={(page) => pagination.page = page}
            onsort={(field, order) => {sort = {field, order};}}
            onpagelengthchange={(newLimit) => pagination.limit = newLimit}
            {columns}
            data={trainingNotes}
            expandableRows
            expandableRowsComponent={expansionSnippet}
    >
    </FlexList>
</wa-details>

{#snippet detailsSummary(title, iconVariant, available = 0)}
    <div slot="summary">
        <div class="d-inline-flex align-items-center">
            <h2 class="m-0">
                <Icon icon="graduation-cap" class="me-1" variant={iconVariant}/>
                {title}
            </h2>
            {#if available > 0}
                <div class="ms-3 fs-5">
                    <wa-badge variant="danger" appearance="filled">{available} available</wa-badge>
                </div>
            {/if}
        </div>
    </div>
    <div slot="expand-icon">
        <Icon icon="square-plus" variant="light"/>
    </div>
    <div slot="collapse-icon">
        <Icon icon="square-minus" variant="regular"/>
    </div>
{/snippet}

{#snippet expansionSnippet(/** @type {TrainingNote} */ notification)}
    <wa-callout appearance="plain" variant="neutral" class="rounded-0">
        <Row>
            <Col class="col-12 col-md-6 col-lg-4">
                <div class="d-flex">
                    <div class="fw-bold me-3">
                        Date:
                    </div>
                    <div>
                        {notification.training_date}
                    </div>
                </div>
            </Col>
            <Col class="col-12 col-md-6 col-lg-4">
                <div class="d-flex">
                    <div class="fw-bold me-3">
                        Start Time:
                    </div>
                    <div>
                        {notification.start_time}
                    </div>
                </div>
            </Col>
            <Col class="col-12 col-md-6 col-lg-4">
                <div class="d-flex">
                    <div class="fw-bold me-3">
                        End Time:
                    </div>
                    <div>
                        {notification.end_time}
                    </div>
                </div>
            </Col>
        </Row>
        <wa-divider></wa-divider>
        <div>
            <div class="d-flex">
                <div class="fw-bold me-3">
                    Topic:
                </div>
                <div>
                    {notification.topic}
                </div>
            </div>
        </div>
        <div>
            <div class="d-flex">
                <div class="fw-bold me-3">
                    Subtopic:
                </div>
                <div>
                    {notification.subtopic}
                </div>
            </div>
        </div>
        <wa-divider></wa-divider>
        <Row>
            <Col class="col-12 col-md-6">
                <div class="d-flex">
                    <div class="fw-bold me-3">
                        Trainer:
                    </div>
                    <div>
                        {notification.trainer}
                    </div>
                </div>
            </Col>
            <Col class="col-12 col-md-6">
                <div class="d-flex">
                    <div class="fw-bold me-3">
                        Person:
                    </div>
                    <div>
                        {notification.person}
                    </div>
                </div>
            </Col>
        </Row>
        <wa-divider></wa-divider>
        <div class="fw-bold">
            Note:
        </div>
        <div>
            {notification.details}
        </div>
    </wa-callout>
{/snippet}